import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Grid, LinearProgress, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import DetailsCard from '../../../ui/layout/DetailsCard';
import ActionButton from '../../../ui/primitives/ActionButton';
import Loader from '../../../ui/primitives/Loader';
import { getRepositoryHealthInsights } from '../../../modules/dashboard';
import QUERY_KEYS from '../../../query/queryKeys';

const DONUT_SIZE = 220;

const RepositoryHealthCard = ({ sx = {} }) => {
  const { projectId } = useParams();
  const theme = useTheme();

  const {
    data,
    isLoading: loading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: QUERY_KEYS.project.repositoryHealthInsights(projectId),
    queryFn: () => getRepositoryHealthInsights(projectId),
    enabled: !!projectId,
  });

  const ageDistribution = data?.age_distribution;
  const lastModified = data?.last_modified;
  const insight = data?.insight;

  const ageChartData = useMemo(() => {
    if (!ageDistribution) return [];
    return [
      {
        key: 'newlyCreated',
        name: 'Newly Created (< 6 months)',
        value: ageDistribution.newly_created,
        color: theme.palette.success.main,
      },
      {
        key: 'moderatelyAged',
        name: 'Moderately Aged (6-12 months)',
        value: ageDistribution.moderately_aged,
        color: theme.palette.warning.main,
      },
      {
        key: 'oldStale',
        name: 'Old / Stale (> 1 year)',
        value: ageDistribution.old_stale,
        color: theme.palette.error.main,
      },
    ];
  }, [ageDistribution, theme]);

  const hasAgeData = ageChartData.some((entry) => entry.value > 0);

  const recentlyUpdatedPct = lastModified?.recently_updated_percentage ?? 0;
  const longUnmodifiedPct = lastModified?.long_unmodified_percentage ?? 0;

  if (loading) {
    return (
      <DetailsCard title="Repository Health Overview" sx={{ ...sx, overflow: 'visible' }}>
      <Loader minHeight={320} />
      </DetailsCard>
    );
  }

  if (isError) {
    return (
      <DetailsCard title="Repository Health Overview" ssx={{ ...sx, overflow: 'visible' }}>
        <Alert severity="error" action={<ActionButton label="Retry" onClick={() => refetch()} />}>
          {error?.message || 'Failed to load repository health insights.'}
        </Alert>
      </DetailsCard>
    );
  }

  return (
    <DetailsCard title="Repository Health Overview" sx={{ ...sx, overflow: 'visible' }}>
      <Grid container spacing={3}>
        {/* Last modified bars + AI insight */}
        <Grid item xs={12} sm={6}>
          <Stack spacing={2.5}>
            <Box>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  Recently Updated
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {recentlyUpdatedPct}% ({lastModified?.recently_updated ?? 0})
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={recentlyUpdatedPct}
                color="success"
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" color="text.secondary">
                Updated within last 90 days
              </Typography>
            </Box>

            <Box>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  Long Unmodified
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {longUnmodifiedPct}% ({lastModified?.long_unmodified ?? 0})
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={longUnmodifiedPct}
                color="error"
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" color="text.secondary">
                Not updated in over 90 days
              </Typography>
            </Box>

            {insight?.message && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: (t) => `${t.shape.borderRadius}px`,
                  bgcolor: (t) => alpha(t.palette.info.main, 0.06),
                  border: '1px solid',
                  borderColor: (t) => alpha(t.palette.info.main, 0.2),
                }}
              >
                <Typography variant="caption" fontWeight={700} color="info.dark" display="block" sx={{ mb: 0.25 }}>
                  AI Insight
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {insight.message}
                </Typography>
              </Box>
            )}
          </Stack>
        </Grid>

        {/* Age distribution donut */}
        <Grid item xs={12} sm={6} sx={{ overflow: 'visible' }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            Age Distribution
          </Typography>
          {hasAgeData ? (
            <Stack spacing={2} alignItems="center">
              <Box sx={{ width: DONUT_SIZE, height: DONUT_SIZE, verflow: 'visible'  }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={ageChartData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={56}
                      outerRadius={90}
                      paddingAngle={2}
                    >
                      {ageChartData.map((entry) => (
                        <Cell key={entry.key} fill={entry.color} />
                      ))}
                    </Pie>
                   <Tooltip
                      allowEscapeViewBox={{ x: true, y: true }}
                      wrapperStyle={{ zIndex: 1300, pointerEvents: 'none' }}
                      contentStyle={{
                        borderRadius: theme.shape.borderRadius,
                        borderColor: theme.palette.divider,
                        fontSize: theme.typography.body2.fontSize,
                        whiteSpace: 'nowrap',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Stack
                direction="row"
                flexWrap="wrap"
                columnGap={2.5}
                rowGap={0.75}
                justifyContent="center"
              >
                {ageChartData.map((entry) => (
                  <Stack key={entry.key} direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: entry.color, flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary" whiteSpace="nowrap">
                      {entry.name}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No age distribution data available.
            </Typography>
          )}
        </Grid>
      </Grid>
    </DetailsCard>
  );
};

export default RepositoryHealthCard;